import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { NzTreeNode } from 'ng-zorro-antd/tree';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { NzTreeFlatDataSource, NzTreeFlattener } from 'ng-zorro-antd/tree-view';
import { Store } from '@ngrx/store';
import { Subscription, Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, filter } from 'rxjs/operators';
import { GetProjectSourceCode, GetProjectSourceFileCustomCodes, GetProjectSourceFiles } from '../../project-source-code-data-source/project-source-code.actions';
import { ProjectSourceCode, ProjectSourceFile } from '../../project-source-code-data-source/project-source-code.interfaces';
import { TreeNode } from './project-source-files';
import { ProjectInterface, ProjectModel } from '@rappider/api-sdk';
import {
  selectActiveProjectFileSourceCode,
  selectProjectSourceCodeState,
  selectProjectSourceFiles,
  selectIsFilesListLoading,
  selectIsFileContentLoading
} from '../../project-source-code-data-source/project-source-code.selectors';

export interface FlatNode {
  expandable: boolean;
  name: string;
  level: number;
  fullPath?: string;
}

@Component({
  selector: 'rappider-project-source-files',
  templateUrl: './project-source-files.component.html',
  styleUrls: ['./project-source-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectSourceFilesComponent implements OnInit, OnDestroy {

  @Output() pathChanged = new EventEmitter<NzTreeNode>();

  activatedNode?: NzTreeNode;
  activeSourceCodeKey?: string;
  projectSourceFiles: ProjectSourceFile[] = [];
  projectSourceFilesTreeData: NzTreeNode[] = [];
  sourceFileTree: TreeNode[] = [];
  activeProject: ProjectInterface | null = null;
  treeData: any[] = [];
  selectListSelection = new SelectionModel<FlatNode>();
  showLeafIcon = false;

  private isFilesLoading = false;
  private hasTreeData = false;
  private isProjectSourceCodeFilesInitialized = false;

  /** Controls tree visibility based on data availability and loading state */
  get showTree(): boolean {
    return this.hasTreeData && !this.isFilesLoading;
  }

  searchWord = '';
  searchTextBoxConfig = {
    suffixIcon: {
      name: 'fa-regular fa-magnifying-glass',
    },
    textbox: {
      placeholder: 'Search files',
    }
  };

  private subscriptions: Subscription[] = [];
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<any>,
    private cdr: ChangeDetectorRef
  ) { }

  /** Initialize component and load project source files */
  ngOnInit(): void {
    this.refreshProjectSourceCodeFiles();
    this.subscriptions = [
      this.subscribeToActiveProject(),
      this.subscribeToActiveProjectFileSourceCode()
    ];
  }

  /** Clean up subscriptions and observables */
  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Subscribe to active project changes and initialize data flow */
  private subscribeToActiveProject(): Subscription {
    return this.store.select(state => state.activeProject.data)
      .pipe(takeUntil(this.destroy$))
      .subscribe((activeProject: ProjectInterface) => {
        if (activeProject?.id) {
          this.activeProject = activeProject;
          this.subscribeToDataSources();
        }
      });
  }

  /** Combine and manage reactive data sources for optimal performance */
  private subscribeToDataSources(): void {
    const dataSubscription = combineLatest([
      this.store.select(selectProjectSourceFiles),
      this.store.select(selectIsFilesListLoading)
    ]).pipe(
      distinctUntilChanged(([prevFiles, prevLoading], [currFiles, currLoading]) =>
        this.arraysEqual(prevFiles, currFiles) && prevLoading === currLoading
      ),
      takeUntil(this.destroy$)
    ).subscribe(([projectSourceFiles, isLoading]) => {
      this.handleDataChange(projectSourceFiles, isLoading);
    });

    this.subscriptions.push(dataSubscription);
  }

  /** Process data changes and update component state */
  private handleDataChange(projectSourceFiles: ProjectSourceFile[] | null, isLoading: boolean): void {
    this.isFilesLoading = isLoading && !projectSourceFiles?.length;

    if (projectSourceFiles && projectSourceFiles.length > 0) {
      this.updateTreeData(projectSourceFiles);
      this.hasTreeData = true;
    } else if (!this.isProjectSourceCodeFilesInitialized) {
      this.initializeProjectSourceCodeFiles();
    }

    this.cdr.markForCheck();
  }

  private subscribeToActiveProjectFileSourceCode(): Subscription {
    return this.store.select(selectActiveProjectFileSourceCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe((activeProjectFileSourceCode: ProjectSourceCode | null) => {
        if (activeProjectFileSourceCode?.fullPath != null) {
        }
      });
  }

  /** Update tree data when source files change */
  private updateTreeData(projectSourceFiles: ProjectSourceFile[]): void {
    if (this.arraysEqual(this.projectSourceFiles, projectSourceFiles)) {
      return;
    }

    this.projectSourceFiles = [...projectSourceFiles];
    this.sourceFileTree = [...projectSourceFiles];
    this.setTreeData(this.sourceFileTree);
  }

  private arraysEqual(a: any[] | null, b: any[] | null): boolean {
    if (!a && !b) {
      return true;
    }
    if (!a || !b) {
      return false;
    }
    if (a.length !== b.length) {
      return false;
    }

    return a.every((item, index) =>
      item.fullPath === b[index]?.fullPath &&
      item.name === b[index]?.name &&
      item.type === b[index]?.type
    );
  }

  private setTreeData(projectSourceFiles: ProjectSourceFile[]): void {
    try {
      const newTreeData = this.mapProjectSourceFilesToTreeData(projectSourceFiles);
      this.projectSourceFilesTreeData = newTreeData;
    } catch (error) {
      this.projectSourceFilesTreeData = [];
    }
  }

  /** Refresh project source files from the store */
  refreshProjectSourceCodeFiles(): void {
    if (this.activeProject?.id) {
      this.isProjectSourceCodeFilesInitialized = true;
      if (!this.projectSourceFiles?.length) {
        this.store.dispatch(GetProjectSourceFiles());
      }
    }
  }

  private initializeProjectSourceCodeFiles(): void {
    this.isProjectSourceCodeFilesInitialized = true;
    setTimeout(() => {
      this.refreshProjectSourceCodeFiles();
    }, 1000);
  }

  /** Handle tree node click events and route to appropriate handlers */
  onTreeNodeClicked(event: any): void {
    if (event?.eventName === 'click') {
      event.event?.preventDefault();
      event.event?.stopPropagation();
    }

    if (event?.node?.origin.type) {
      switch (event?.node?.origin.type) {
        case 'folder':
          this.folderNodeClicked(event?.node?.origin);
          break;
        case 'file':
          this.leafNodeClicked(event?.node?.origin);
          break;
      }
    }
  }

  onSearchFiles(searchText: string): void {
  }

  /** Handle file selection and load source code */
  private leafNodeClicked(node: NzTreeNode): void {
    this.pathChanged.emit(node);
    this.getSourceCode(node?.key);
    this.getCustomCodes(node?.key);
  }

  private folderNodeClicked(node: NzTreeNode): void {
  }

  private getSourceCode(sourceFilePath: string | undefined): void {
    if (sourceFilePath) {
      this.store.dispatch(GetProjectSourceCode({ payload: { sourceFilePath } }));
    }
  }

  private getCustomCodes(sourceFilePath: string | undefined): void {
    if (sourceFilePath) {
      this.store.dispatch(GetProjectSourceFileCustomCodes({ payload: { sourceFilePath } }));
    }
  }

  /** Transform project source files into tree data structure */
  mapProjectSourceFilesToTreeData(
    projectSourceFiles: ProjectSourceFile[],
    visitedKeys = new Set<string>(),
    depth = 0,
    maxDepth = 5
  ): NzTreeNode[] {
    if (depth > maxDepth || !projectSourceFiles || !Array.isArray(projectSourceFiles)) {
      return [];
    }

    try {
      return projectSourceFiles
        .map((projectSourceFile: ProjectSourceFile) => {
          if (!projectSourceFile) {
            return null;
          }

          const key = projectSourceFile?.fullPath || `${projectSourceFile.name}_${depth}_${Date.now()}`;

          if (visitedKeys.has(key)) {
            return null;
          }
          visitedKeys.add(key);

          const treeNode = {
            title: projectSourceFile?.name || '--',
            key,
            type: projectSourceFile?.type,
            icon: projectSourceFile?.type === 'folder' ? null : 'file',
            selected: false,
            isLeaf: projectSourceFile?.type === 'file',
            expanded: projectSourceFile?.type === 'folder',
            children: projectSourceFile?.children?.length
              ? this.mapProjectSourceFilesToTreeData(
                projectSourceFile.children,
                new Set(visitedKeys),
                depth + 1,
                maxDepth
              )
              : []
          } as any;

          return treeNode;
        })
        .filter(Boolean) as NzTreeNode[];
    } catch (error) {
      return [];
    }
  }

  /** Find tree node by full path for selection purposes */
  findNodeByPath(fullPath: string): TreeNode | null {
    const traverseTree = (nodes: TreeNode[], parentPath: string = ''): TreeNode | null => {
      for (const node of nodes) {
        const currentPath = parentPath ? `${parentPath}/${node.name}` : node.name;

        if (currentPath === fullPath) {
          return node;
        }

        if (node.children && node.children.length > 0) {
          const foundNode = traverseTree(node.children, currentPath);
          if (foundNode) {
            return foundNode;
          }
        }
      }
      return null;
    };

    return traverseTree(this.sourceFileTree);
  }
}
